/**
 * Step 1 of the Production Line journey (production-lines): the Business
 * Profile the server drafted, the fields still needed before it can be
 * confirmed, and the brief beside it. Moved verbatim out of
 * ProductionLineDetailPage.tsx.
 */

import type { ReactNode } from 'react';
import type { BusinessProfileDraft } from '@mouja/domain/analysis';

import type { WorkspaceCopy } from '../../merchant/copy.js';
import { missingProfileConfirmationFields } from '../../merchant/profileCompleteness.js';
import { fieldValue } from '../business-profile/BusinessProfilePage.js';
import {
  Button,
  Callout,
  Card,
  DefinitionList,
  Eyebrow,
  Pix,
} from '../../merchant/ui/primitives.js';

export function ProfileStep({
  copy,
  language,
  workspaceName,
  domain,
  prompt,
  websiteUrl,
  payload,
  working,
  onConfirm,
  onEdit,
}: {
  readonly copy: WorkspaceCopy;
  readonly language: 'en' | 'ar';
  readonly workspaceName: string;
  readonly domain: string;
  readonly prompt: string;
  readonly websiteUrl: string | null;
  readonly payload: BusinessProfileDraft;
  readonly working: boolean;
  readonly onConfirm: () => void;
  readonly onEdit: () => void;
}): ReactNode {
  const missing = missingProfileConfirmationFields(payload);
  return (
    <div className="mj-ws-g2 is-journey">
      <Card as="section" padding={24} aria-labelledby="bp-step-h">
        <Eyebrow>
          <Pix status="needs-input" />
          {copy.line.step1}
        </Eyebrow>
        <h2 id="bp-step-h" className="mj-ws-h2 mj-ws-mt-8">
          {copy.line.profileQuestion(workspaceName)}
        </h2>
        <p className="mj-ws-muted mj-ws-sm" style={{ marginTop: 4 }}>
          {copy.line.profileFrom(domain || workspaceName)}
        </p>
        <DefinitionList
          className="mj-ws-mt-20"
          rows={[
            [
              copy.line.businessName,
              fieldValue(payload.identity, copy.common.notEstablished),
            ],
            [
              copy.line.description,
              fieldValue(payload.niche, copy.common.notEstablished),
            ],
            [
              copy.line.geography,
              fieldValue(payload.geography, copy.common.notEstablished),
            ],
            [
              copy.line.offer,
              fieldValue(payload.offers[0]?.title, copy.common.notEstablished),
            ],
            [
              copy.line.source,
              <span key="s" dir="ltr">
                {domain || copy.common.dash}
              </span>,
            ],
          ]}
        />
        {missing.length > 0 ? (
          <Callout className="mj-ws-mt-16 mj-ws-sm">
            <Pix status="needs-input" />
            <span className="mj-ws-callout-copy">
              <strong>{copy.line.stillNeeded}:</strong>{' '}
              {missing
                .map((field) => localizedMissingField(field, language))
                .join(', ')}
            </span>
          </Callout>
        ) : null}
        <div className="mj-ws-row mj-ws-mt-24">
          <Button
            variant="primary"
            disabled={working || missing.length > 0}
            onClick={onConfirm}
          >
            {working ? copy.line.confirming : copy.line.confirmProfile}
          </Button>
          <Button onClick={onEdit} disabled={working}>
            {copy.line.editDetails}
          </Button>
        </div>
      </Card>
      <Card as="aside" className="mj-ws-col" aria-label={copy.line.yourBrief}>
        <Eyebrow>{copy.line.yourBrief}</Eyebrow>
        <DefinitionList
          columns="narrow"
          rows={[
            [
              copy.line.website,
              websiteUrl ? (
                <a key="w" href={websiteUrl} dir="ltr" target="_blank" rel="noreferrer">
                  {websiteUrl}
                </a>
              ) : (
                copy.common.dash
              ),
            ],
            [copy.line.prompt, prompt || copy.common.noPromptYet],
          ]}
        />
        <p className="mj-ws-muted mj-ws-sm">
          {copy.line.confirmingNote(workspaceName)}
        </p>
      </Card>
    </div>
  );
}

function localizedMissingField(field: string, language: 'en' | 'ar'): string {
  if (language === 'en') return field;
  if (field === 'your business name') return 'اسم نشاطك التجاري';
  if (field === 'what your business does') return 'وش يقدمه نشاطك التجاري';
  if (field === 'where your business operates') return 'وين يشتغل نشاطك';
  if (field === 'at least one concrete offer') return 'عرض فعلي واحد على الأقل';
  return field;
}
